// WHY — multi-line CPL chart for the dashboard. Flagged + manually-pinned
// accounts render as bold colored lines; everything else renders as faint
// gray context lines. This keeps 20+ accounts readable without collision.
// Null CPL values break the line into gap segments (interleaved M..L.. runs
// in a single path d-string). Uses hand-rolled SVG — no chart library.
//
// Axis labels render as absolutely-positioned HTML over the SVG so they
// don't distort when the SVG stretches horizontally (the SVG uses
// preserveAspectRatio="none" to fill the container width). Y values map
// to top% and X values to left% — both percentages line up with SVG user
// units because the SVG's rendered height matches its viewBox height and
// its rendered width is the container width on both sides.

import { useMemo } from "react";
import {
  CHART_MARGIN_LEFT,
  CHART_MARGIN_RIGHT,
  CHART_MARGIN_TOP,
  CHART_MARGIN_BOTTOM,
  buildYTicks,
  buildXTickIndices,
  fmtAxisDate,
  fmtAxisValue,
} from "../lib/chartAxes";

export interface ChartAccount {
  account_id: string;
  display_name: string;
  flagged: boolean;
  sparkline: Array<{ date: string; cpl: number | null }>;
}

interface Props {
  accounts: ChartAccount[];
  highlightedIds: Set<string>; // manually pinned by row click
  hoveredId: string | null;    // transient hover
  width?: number;              // default 800
  height?: number;             // default 200
}

const PALETTE = [
  "#d9645c",
  "#d9b14a",
  "#6dbf78",
  "#7eb7d9",
  "#c98ad9",
  "#d99c4a",
  "#a8c97d",
  "#8b9bd9",
] as const;

export function SmartOverlayChart({
  accounts,
  highlightedIds,
  hoveredId,
  width = 800,
  height = 200,
}: Props) {
  // Compute global Y range across all accounts (memoized)
  // Must be above any early return to satisfy Rules of Hooks.
  const { yMin, yMax } = useMemo(() => {
    let globalMin = Infinity;
    let globalMax = -Infinity;
    for (const account of accounts) {
      for (const pt of account.sparkline) {
        if (pt.cpl !== null && Number.isFinite(pt.cpl)) {
          if (pt.cpl < globalMin) globalMin = pt.cpl;
          if (pt.cpl > globalMax) globalMax = pt.cpl;
        }
      }
    }
    if (!Number.isFinite(globalMin)) {
      return { yMin: 0, yMax: 1 };
    }
    const range = globalMax - globalMin;
    const pad = Math.max(range * 0.1, 1);
    return {
      yMin: Math.max(0, globalMin - pad),
      yMax: globalMax + pad,
    };
  }, [accounts]);

  // Empty state — must come after all hook calls.
  const isEmpty =
    accounts.length === 0 || (accounts[0]?.sparkline.length ?? 0) === 0;
  if (isEmpty) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--color-bone-dim)",
          background: "rgba(255,255,255,0.02)",
          borderRadius: 6,
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-xs)",
        }}
      >
        No data in range
      </div>
    );
  }

  const yRange = yMax - yMin || 1;

  const chartLeft = CHART_MARGIN_LEFT;
  const chartRight = width - CHART_MARGIN_RIGHT;
  const chartTop = CHART_MARGIN_TOP;
  const chartBottom = height - CHART_MARGIN_BOTTOM;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const yScale = (v: number): number =>
    chartTop + ((yMax - v) / yRange) * chartHeight;

  const days = accounts[0]!.sparkline.length;
  const xScale = (i: number): number =>
    chartLeft + (i / Math.max(days - 1, 1)) * chartWidth;

  const buildPath = (sparkline: ChartAccount["sparkline"]): string => {
    let d = "";
    let inSegment = false;

    for (let i = 0; i < days; i++) {
      const pt = sparkline[i];
      if (!pt || pt.cpl === null || !Number.isFinite(pt.cpl)) {
        inSegment = false;
        continue;
      }
      const x = xScale(i);
      const y = yScale(pt.cpl);
      if (!inSegment) {
        d += `M ${x} ${y}`;
        inSegment = true;
      } else {
        d += ` L ${x} ${y}`;
      }
    }
    return d;
  };

  const yTicks = buildYTicks(yMin, yMax);
  const xTickIndices = buildXTickIndices(days);

  const axisLabelStyle = {
    position: "absolute" as const,
    fontSize: "var(--fs-xs)",
    fontFamily: "var(--font-mono)",
    color: "var(--color-bone-dim)",
    pointerEvents: "none" as const,
    userSelect: "none" as const,
    lineHeight: 1,
  };

  // Separate colored vs gray accounts; assign palette colors to colored ones
  const coloredAccounts: Array<{ account: ChartAccount; color: string }> = [];
  const grayAccounts: ChartAccount[] = [];

  let paletteIndex = 0;
  for (const account of accounts) {
    const isColored =
      account.flagged || highlightedIds.has(account.account_id);
    if (isColored) {
      coloredAccounts.push({
        account,
        color: PALETTE[paletteIndex % PALETTE.length] as string,
      });
      paletteIndex++;
    } else {
      grayAccounts.push(account);
    }
  }

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        borderRadius: 6,
        padding: 12,
      }}
    >
      <div style={{ position: "relative", height }}>
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          style={{ display: "block" }}
        >
          {/* Y gridlines — faint horizontals at each tick value */}
          {yTicks.map((tick) => (
            <line
              key={`grid-${tick}`}
              x1={chartLeft}
              x2={chartRight}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* X-axis baseline */}
          <line
            x1={chartLeft}
            x2={chartRight}
            y1={chartBottom}
            y2={chartBottom}
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          {/* Gray context lines rendered first so colored lines appear on top */}
          {grayAccounts.map((a) => {
            const d = buildPath(a.sparkline);
            if (!d) return null;
            return (
              <path
                key={a.account_id}
                d={d}
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={1}
                opacity={hoveredId === a.account_id ? 0.6 : 1}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}

          {/* Colored (flagged / highlighted) lines rendered on top */}
          {coloredAccounts.map(({ account: a, color }) => {
            const d = buildPath(a.sparkline);
            if (!d) return null;
            const isHover = hoveredId === a.account_id;
            return (
              <path
                key={a.account_id}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={isHover ? 2.5 : 1.5}
                opacity={isHover || !hoveredId ? 1 : 0.55}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
        </svg>

        {/* Y-axis labels overlay — % positioning maps to SVG user coords
            because the SVG height matches its viewBox height. */}
        {yTicks.map((tick) => (
          <span
            key={`y-${tick}`}
            style={{
              ...axisLabelStyle,
              left: 0,
              top: `${(yScale(tick) / height) * 100}%`,
              transform: "translateY(-50%)",
              width: CHART_MARGIN_LEFT - 6,
              textAlign: "right",
            }}
          >
            {fmtAxisValue(tick, yRange)}
          </span>
        ))}

        {/* X-axis labels overlay at the bottom */}
        {xTickIndices.map((idx) => {
          const dateStr = accounts[0]?.sparkline[idx]?.date ?? "";
          return (
            <span
              key={`x-${idx}`}
              style={{
                ...axisLabelStyle,
                top: chartBottom + 6,
                left: `${(xScale(idx) / width) * 100}%`,
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
              }}
            >
              {fmtAxisDate(dateStr)}
            </span>
          );
        })}
      </div>

      {/* Legend — only rendered when there are colored accounts */}
      {coloredAccounts.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            marginTop: 8,
          }}
        >
          {coloredAccounts.map(({ account: a, color }) => (
            <span
              key={a.account_id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: "var(--fs-xs)",
                fontFamily: "var(--font-mono)",
                color: "var(--color-bone)",
                opacity: 0.85,
                marginRight: 12,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 2,
                  background: color,
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              />
              {a.display_name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
